import { TypeAheadFilter } from '@components/table/filters/TypeAheadFilter';
import { TableFilterTypeAheadFilterExtendedProps } from '@components/table/filters/TypeaheadFilterProps.type';

import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { BrokerageUserRole } from '@lio/rule-engine';

import { useSearchPaginatedBrokerageUsers } from '@services/brokerage';
import { useEntityMetadata } from '@services/entityMetadata';

import { handleScrollToBottom } from '@utils/HandleScrollToBottom';
import { useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

export interface BrokerageProducerTypeaheadFilterProps extends TableFilterTypeAheadFilterExtendedProps {}

export const BrokerageProducerTypeaheadFilter = (componentProps: BrokerageProducerTypeaheadFilterProps) => {
  const { value, disabled, watchedField, selectedValues = {} } = componentProps;
  const [selectedFromList, setSelectedFromList] = useState(false);
  const [searchText, setSearchText] = useState('');

  // will be a value when selected from list - but just a string if loaded from shared url
  const selectedBrokerages = watchedField ? selectedValues?.[watchedField] : undefined;

  const brokerageId = useMemo(() => {
    if (!selectedBrokerages) {
      return undefined;
    }

    if (!Array.isArray(selectedBrokerages)) {
      return selectedBrokerages.value ?? selectedBrokerages;
    }

    if (selectedBrokerages.length === 1) {
      return selectedBrokerages[0]?.value ?? selectedBrokerages[0];
    }
    if (selectedBrokerages.length > 1) {
      // Multiple is not supported
      return undefined;
    }
  }, [selectedBrokerages]);

  const { brokerageUsers, fetchNextPage, isFetching, isLoading, isPending, isFetchingNextPage } =
    useSearchPaginatedBrokerageUsers(
      brokerageId ? { brokerageId } : undefined,
      {
        role: BrokerageUserRole.Producer,
        sort: '+user.firstName, +user.lastName',
        pageLimit: 25,
        searchText: searchText,
      },
      { enabled: !!brokerageId },
    );

  useEffect(() => {
    if (brokerageId) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerageId]);

  // this means filter has been applied by the caller but we need to ensure we have the label since this filter is paginated
  const { entityMetadata } = useEntityMetadata(
    {
      'brokerageProducer.id': [value],
    },
    !selectedFromList && !!value ? true : false,
  );

  const selectedItemLabel = useMemo(() => {
    const producer = entityMetadata?.brokerageProducer?.[0];
    if (producer) {
      return `${producer.label}`;
    }
  }, [entityMetadata]);

  const handleDebouncedInputChange = useDebouncedCallback((value) => {
    setSearchText(value);
  }, DEBOUNCE_DELAYS.FORM_TYPING);

  const items = !brokerageId
    ? []
    : (brokerageUsers?.pages
        ?.flatMap((page) => page.items)
        ?.map((item) => {
          return {
            key: item.brokerageProducers?.[0]?.id ?? `${item.user.firstName} ${item.user.lastName}`,
            value: item.brokerageProducers?.[0]?.id,
            label: `${item.user.firstName} ${item.user.lastName}`,
            subLabel: item.user.email,
          };
        }) ?? []);

  useEffect(() => {
    if (!value) {
      setSearchText('');
    }
  }, [value]);

  return (
    <TypeAheadFilter
      value={value}
      defaultValue={value}
      disabled={disabled || (!value && items.length === 0)}
      label={componentProps.label}
      options={items ?? []}
      isMultiple={componentProps?.fieldConfig?.selectField?.multiSelect}
      limitTags={1}
      selectedItemDefaultLabel={!selectedFromList ? selectedItemLabel : undefined}
      onChange={(value: any) => {
        setSelectedFromList(true);
        componentProps.onChange(value);
      }}
      helperText={selectedBrokerages?.length > 1 ? 'Only available if one brokerage is selected' : undefined}
      onInputChange={(_, value) => {
        handleDebouncedInputChange(value);
      }}
      loading={isLoading || isFetching || isFetchingNextPage || isPending}
      slotProps={{
        listbox: {
          onScroll: (e) => {
            handleScrollToBottom(e, fetchNextPage);
          },
          style: {
            maxHeight: '300px',
          },
        },
      }}
    />
  );
};
